// API 配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const API_CONFIG = {
    BASE_URL: API_BASE_URL,
    CLIENT_VERSION: '202511131630',
};

// 商品类型定义
export const PRODUCT_TYPE = {
    // 促销商品（带活动折扣）
    NEW_STORE_COIN: 1100, // 金币促销商品
    NEW_STORE_GEM: 1200, // 钻石促销商品
    NEW_STORE_BOOST: 1300, // 道具促销商品

    // 基准商品（不参与活动折扣）
    BASE_COIN: 1101, // 金币基准商品（9.99 USD）
    BASE_GEM: 1201, // 钻石基准商品（9.99 USD）

    // 旧版商品类型（兼容）
    STORE_COIN: 1,
    STORE_GEM: 100,
    STORE_BOOST: 17,
    LUCKY_SPIN: 12,
};

// 促销活动类型定义
export const ACTIVITY_TYPE = {
    COIN_SALE: 100, // 金币促销
    GEM_SALE: 101, // 钻石促销
    COMBINED_SALE: 104, // 金币钻石二合一促销
};

// API 端点
export const API_ENDPOINTS = {
    // 认证相关
    REGISTER_ACCOUNT: '/auth/RegisterA10Account',
    LOGIN_A10: '/login/auto/msg.LoginA10Request',
    LOGIN_FACEBOOK: '/login/auto/msg.LoginFacebookCanvasRequest',

    // 支付相关
    PAYPAL_CREATE_ORDER: '/payment/auto/msg.PaypalPaymentOrderRequest',
    PAYPAL_VERIFY: '/payment/auto/msg.PaypalPaymentVerifyPurchaseRequest',
    FACEBOOK_CREATE_ORDER: '/payment/auto/msg.PaymentCanvasOrderRequest',
    FACEBOOK_VERIFY: '/payment/auto/msg.FacebookPaymentVerifyPurchaseRequest',
    FACEBOOK_PRODUCT_INFO: '/fb/canvas_product_Info',

    // 商店商品列表
    GET_PRODUCT_LIST: '/lobby_batch/auto/msg.PaymentCanvasProductRequest',
    // 当前玩家信息（用于登录后刷新顶部展示数据）- gateway 路由
    GET_PLAYER_INFO_GATEWAY: '/gateway/auto/msg.PlayerRequest',
    // 当前玩家信息（用于登录后刷新顶部展示数据）- lobby_batch 路由
    GET_PLAYER_INFO: '/lobby_batch/auto/msg.PlayerRequest',

    // 促销活动相关
    GET_TYPEC_ACTIVITY_LIST: '/lobby_batch/auto/msg.GetTypeCActivityListRequest',
};

// 序列号管理
let sequenceId = 1;

// 获取下一个序列号
export const getNextSequenceId = () => {
    return sequenceId++;
};

// 获取通用的请求头（带序列号参数）
export const getCommonHeaders = (additionalHeaders = {}, sequenceIdValue = null) => {
    return {
        'client-version': API_CONFIG.CLIENT_VERSION,
        'x-sandwich-client-sequence-id': (sequenceIdValue || getNextSequenceId()).toString(),
        ...additionalHeaders,
    };
};

// 获取完整的 API URL
export const getApiUrl = (endpoint) => {
    if (!API_CONFIG.BASE_URL) {
        throw new Error('VITE_API_BASE_URL is not configured for this environment');
    }
    return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// 带重试的 fetch 请求
export const fetchWithRetry = async (url, options, maxRetries = 3) => {
    // 获取序列号，重试时使用相同的序列号
    const currentSequenceId = getNextSequenceId();

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            // 更新 headers，使用固定的序列号
            const headers = options.headers || {};
            const updatedHeaders = {
                ...headers,
                'client-version': API_CONFIG.CLIENT_VERSION,
                'x-sandwich-client-sequence-id': currentSequenceId.toString(),
            };

            const response = await fetch(url, {
                ...options,
                headers: updatedHeaders,
            });

            // 检查响应内容是否包含错误码1004
            if (response.ok) {
                const responseClone = response.clone();
                try {
                    const responseData = await responseClone.json();
                    if (responseData.code === 1004 || responseData.Code === 1004) {
                        console.error('Session expired (code 1004), clearing user and showing refresh dialog');
                        // 动态导入store以避免循环依赖
                        const useStore = (await import('../store/useStore')).default;
                        // 先清除用户状态，防止刷新后自动用过期token登录
                        useStore.getState().logout();
                        // 设置sessionStorage标记，用于处理浏览器刷新按钮的情况
                        sessionStorage.setItem('sessionExpired', 'true');
                        useStore.getState().setRefreshDialogOpen(true);
                        useStore.getState().setApiLoading(false);
                    }
                } catch {
                    // 如果不是JSON响应，忽略错误
                }
                return response;
            }

            // 如果请求成功或者是客户端错误（4xx），不需要重试
            if (response.status >= 400 && response.status < 500) {
                // 即使是4xx错误，也检查是否包含错误码1004
                const responseClone = response.clone();
                try {
                    const responseData = await responseClone.json();
                    if (responseData.code === 1004 || responseData.Code === 1004) {
                        console.error('Session expired (code 1004), clearing user and showing refresh dialog');
                        const useStore = (await import('../store/useStore')).default;
                        // 先清除用户状态，防止刷新后自动用过期token登录
                        useStore.getState().logout();
                        useStore.getState().setRefreshDialogOpen(true);
                        useStore.getState().setApiLoading(false);
                    }
                } catch {
                    // 如果不是JSON响应，忽略错误
                }
                return response;
            }

            // 服务器错误（5xx）或网络错误，进行重试
            if (attempt < maxRetries) {
                console.warn(`Request failed with status ${response.status}, retrying in ${attempt} seconds...`);
                await new Promise((resolve) => setTimeout(resolve, attempt * 1000)); // 递增延迟
            } else {
                return response; // 最后一次尝试，返回失败的响应
            }
        } catch (error) {
            console.error(`Request error on attempt ${attempt}:`, error);

            if (attempt < maxRetries) {
                console.warn(`Request failed with error, retrying in ${attempt} seconds...`);
                await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
            } else {
                throw error; // 最后一次尝试，抛出错误
            }
        }
    }
};
